/**
 * As frases de erro do admin legado, portadas como funções puras, palavra por palavra.
 *
 * POR QUE PURAS: o legado mistura a tradução com efeito (o `traduzirErro` de `admin.html:3344`
 * escreve no console antes de responder). Aqui a tradução só recebe texto e devolve texto, e por
 * isso roda na suíte de unidade em milissegundos, com cada padrão provado um por um. Quem quiser o
 * registro no console faz na borda, com a mensagem original que o `ErroDeDados` também carrega.
 *
 * NENHUMA FRASE FOI MELHORADA. Migração é reproduzir o legado idêntico, e texto diferente é
 * comportamento diferente para quem lê a tela.
 */

// A resposta de qualquer mensagem que nenhum padrão reconhece, e também a da mensagem vazia.
// Fica numa constante porque aparece duas vezes na função do legado, e as duas precisam bater.
export const FRASE_GENERICA = 'Algo deu errado. Tente novamente.';

/**
 * A tradução do legado (`admin.html:3344` a `3355`), na mesma ordem de teste.
 *
 * A ORDEM IMPORTA e foi mantida: uma mensagem pode casar com dois padrões, e quem vence é o primeiro.
 * "invalid token" em rede, por exemplo, cai na frase do link expirado, e não na de conexão.
 */
export function traduzirErro(msg: string | null | undefined): string {
  // Sem mensagem, a frase genérica (admin.html:3345).
  if (!msg) return FRASE_GENERICA;
  // Senha ou e-mail errados no login por senha (admin.html:3347).
  if (/Invalid login credentials/i.test(msg)) return 'E-mail ou senha incorretos.';
  // Conta criada e ainda não confirmada por e-mail (admin.html:3348).
  if (/Email not confirmed/i.test(msg)) return 'Confirme seu e-mail antes de entrar.';
  // Link de recuperação ou de confirmação vencido ou já usado (admin.html:3349).
  if (/Token has expired or is invalid/i.test(msg) || /invalid.*token/i.test(msg)) {
    return 'Esse link expirou ou já foi usado. Peça um novo.';
  }
  // Sessão perdida no meio do uso (admin.html:3350).
  if (/Auth session missing/i.test(msg)) return 'Sua sessão expirou. Faça login de novo.';
  // Os três jeitos que o Supabase Auth tem de dizer "devagar" (admin.html:3351).
  if (/rate limit/i.test(msg) || /too many requests/i.test(msg) || /you can only request this after/i.test(msg)) {
    return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.';
  }
  // Recusa do RLS: a policy barrou a operação (admin.html:3352).
  if (/row-level security/i.test(msg) || /policy/i.test(msg)) return 'Sem permissão para essa operação.';
  // Falha de rede, inclusive o "Failed to fetch" do navegador (admin.html:3353).
  if (/network|fetch|connection/i.test(msg)) return 'Erro de conexão. Verifique sua internet.';
  // Nada reconheceu: a genérica, como a última linha do legado (admin.html:3354).
  return FRASE_GENERICA;
}

/**
 * Os códigos que a Edge Function `enviar-email-atendimento` devolve no corpo do erro, e a frase
 * curta de cada um, copiados de `admin.html` (`ERROS_ENVIO_EMAIL`).
 *
 * A seta "→" em duas frases é do legado e ficou: trocar o texto seria melhoria, e melhoria é
 * decisão do fundador (registrada em COBERTURA.md, na lista de itens para ele).
 */
export const ERROS_ENVIO_EMAIL: Readonly<Record<string, string>> = Object.freeze({
  nao_autenticado: 'Sua sessão expirou. Saia e entre no painel de novo.',
  sem_permissao: 'Seu acesso não pode enviar e-mail. Só administrador envia.',
  campos_ausentes: 'Não deu para identificar a cliente ou o produto. Reabra a ficha e tente de novo.',
  lead_invalido: 'Não deu para identificar o atendimento da cliente. Reabra a ficha e tente de novo.',
  cliente_sem_email: 'Cliente sem e-mail cadastrado. Preencha o e-mail no questionário ou no lead.',
  email_invalido: 'O e-mail cadastrado da cliente parece inválido. Corrija no cadastro dela.',
  produto_sem_arquivo: 'Nenhum arquivo configurado para este produto em Sistema → Parâmetros.',
  arquivo_indisponivel: 'Arquivo não encontrado no Storage. Reenvie o material em Sistema → Parâmetros.',
  smtp_nao_configurado: 'Servidor de e-mail não configurado. Avise o suporte técnico.',
  falha_envio: 'O servidor de e-mail recusou o envio. Tente de novo em alguns minutos.',
});

/** O que o erro de uma Edge Function carrega para as funções abaixo decidirem a frase. */
export interface ErroDeFuncaoParaFrase {
  // O `error.message` do supabase-js, que para função é sempre o genérico "non-2xx status code".
  readonly mensagem: string;
  // O status HTTP da resposta da função, quando houve resposta.
  readonly status: number | null;
  // O corpo JSON da resposta de erro, quando veio em JSON.
  readonly corpo: { readonly error?: unknown; readonly codigo?: unknown } | null;
}

/**
 * A frase do erro de envio do e-mail de atendimento (`mensagemErroEnvioEmail` do legado), sem a
 * leitura assíncrona do corpo: quem chama a função já leu o corpo e o entrega aqui.
 *
 * Devolve a frase `curta`, que vai para a tela, e o `detalhe`, que o legado põe no `title` do
 * status para conferência, como no original.
 */
export function mensagemErroEnvioEmail(erro: ErroDeFuncaoParaFrase): { curta: string; detalhe: string } {
  // O código vem do corpo; sem corpo ou sem código, texto vazio, como o `let codigo = ''` do legado.
  const codigo = typeof erro.corpo?.codigo === 'string' && erro.corpo.codigo ? erro.corpo.codigo : '';
  // O detalhe começa pela mensagem genérica e é trocado pelo `error` do corpo quando ele existe.
  const detalhe = typeof erro.corpo?.error === 'string' && erro.corpo.error ? erro.corpo.error : erro.mensagem || '';
  // Código conhecido: a frase dele, que é a mais específica.
  const frase = ERROS_ENVIO_EMAIL[codigo];
  if (frase) return { curta: frase, detalhe };
  // 404 sem código: a função não está publicada no projeto (o gateway responde sem JSON).
  if (erro.status === 404) return { curta: 'Envio indisponível: a função de e-mail não está publicada no servidor.', detalhe };
  // 401 e 403 sem código: o papel da pessoa não pode enviar.
  if (erro.status === 401 || erro.status === 403) {
    return { curta: 'Seu acesso não pode enviar e-mail. Só administrador envia.', detalhe };
  }
  // O resto: o detalhe cru depois do prefixo, e "falha inesperada." quando nem ele existe.
  return { curta: 'Erro ao enviar: ' + (detalhe || 'falha inesperada.'), detalhe };
}

/**
 * O motivo real de uma falha de Edge Function, como a Leitura Estratégica faz (`admin.html:6326`
 * a `6331`): o `error` do corpo quando veio, e a mensagem genérica do supabase-js quando não.
 */
export function motivoDoErroDeFuncao(erro: ErroDeFuncaoParaFrase): string {
  // O legado testa verdade (`if (corpo?.error)`), então texto vazio no corpo não substitui a mensagem.
  return typeof erro.corpo?.error === 'string' && erro.corpo.error ? erro.corpo.error : erro.mensagem;
}
